import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

// REMOVE THIS LINE - it's causing the error:
// const { user, isAuthenticated } = useAuth();

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket must be used within SocketProvider');
    }
    return context;
};

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [connected, setConnected] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const { user, isAuthenticated } = useAuth(); // This is correct - inside component
    const reconnectAttempts = useRef(0);
    const maxReconnectAttempts = 5;
    const socketRef = useRef(null);
    const initialConnectionDone = useRef(false);

    useEffect(() => {
        // Only connect if authenticated and user exists
        if (!isAuthenticated || !user) {
            console.log('Not authenticated, skipping WebSocket connection');
            return;
        }
        
        // For admin users, skip WebSocket connection to avoid issues
        if (user.role !== 'user') {
            console.log('Skipping WebSocket connection for non-user role:', user.role);
            return;
        }

        // Prevent multiple connections
        if (socketRef.current) {
            return;
        }
        
  
const newSocket = io({
    auth: {
        userId: user._id
    },
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: maxReconnectAttempts,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000
});


        
        socketRef.current = newSocket;
        
        // Connection event handlers
        newSocket.on('connect', () => {
            console.log('🔌 WebSocket connected');
            setConnected(true);
            reconnectAttempts.current = 0;
            
            // Request initial data only once
            if (!initialConnectionDone.current) {
                newSocket.emit('request-update', { type: 'initial' });
                initialConnectionDone.current = true;
            }
        });

        newSocket.on('disconnect', (reason) => {
            console.log('🔌 WebSocket disconnected:', reason);
            setConnected(false);
        });

        newSocket.on('connect_error', (error) => {
            console.error('WebSocket connection error:', error);
            reconnectAttempts.current++;
            
            if (reconnectAttempts.current >= maxReconnectAttempts) {
                console.log('Max reconnection attempts reached');
                newSocket.disconnect();
            }
        });

        // Listen for balance updates
        newSocket.on('balance-update', (data) => {
            console.log('💰 Balance update received:', data);
            setLastUpdate(data);
        });

        // Listen for wallet updates
        newSocket.on('wallet-update', (data) => {
            console.log('💼 Wallet update received:', data);
            setLastUpdate(data);
        });

        // Listen for dashboard updates
        newSocket.on('dashboard-update', (data) => {
            console.log('📊 Dashboard update received:', data);
            setLastUpdate(data);
        });

        // Listen for user-specific updates
        newSocket.on('user-update', (data) => {
            console.log('👤 User update received:', data);
            setLastUpdate(data);
        });

        // Ping/pong for connection health check
        newSocket.on('ping', () => {
            if (newSocket.connected) {
                newSocket.emit('pong');
            }
        });

        setSocket(newSocket);

        // Cleanup on unmount
        return () => {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
                socketRef.current = null;
                setSocket(null);
                setConnected(false);
                initialConnectionDone.current = false;
            }
        };
    }, [isAuthenticated, user]);

    // Function to emit events
    const emit = (event, data) => {
        if (socketRef.current && connected) {
            socketRef.current.emit(event, data);
        } else {
            console.warn('Socket not connected, cannot emit:', event);
        }
    };

    // Function to monitor downline users
    const monitorDownline = (downlineIds) => {
        if (socketRef.current && connected && downlineIds.length > 0) {
            socketRef.current.emit('monitor-downline', downlineIds);
        }
    };

    // Function to request manual update
    const requestUpdate = (type = 'full') => {
        emit('request-update', { type, timestamp: Date.now() });
    };

    const value = {
        socket,
        connected,
        lastUpdate,
        emit,
        monitorDownline,
        requestUpdate
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};